import { useEffect, useRef } from 'react'
import { makeProductDetailURL } from '@insight/toolkit-react/lib/PDPModal/PDPHelpers'
import { CONTENTMAP } from '../constants'

/**
 * On initial landing on the products tab, redirects straight to the PDP if the
 * search returns exactly one product AND the query matches that product's
 * materialId (i.e., the user searched by exact SKU).
 *
 * Guarded to fire at most once per page load — never re-triggers on subsequent
 * facet/sort/pagination-driven re-searches, even if those also narrow results to 1.
 *
 * @param {object} params
 * @param {string}  params.tabType
 * @param {boolean} params.firstSearchExecuted
 * @param {boolean} params.isSearching
 * @param {Array}   params.products
 */
export function useSingleResultRedirect({ tabType, firstSearchExecuted, isSearching, products }) {
  const hasHandledInitialResultsRef = useRef(false)

  useEffect(() => {
    if (tabType !== CONTENTMAP.products.id) return
    if (!firstSearchExecuted || isSearching) return
    if (hasHandledInitialResultsRef.current) return
    hasHandledInitialResultsRef.current = true

    if (products.length !== 1) return

    const params = new URLSearchParams(window.location.search)
    const keyword = params.has('q') && params.get('q')
    const { description, materialId, manufacturerName, manufacturerPartNumber } = products[0]

    if (!keyword || keyword.toLowerCase() !== materialId?.toLowerCase()) return

    const preparedProductURL = makeProductDetailURL({
      materialId,
      mfrName: manufacturerName,
      description,
      manufacturerId: manufacturerPartNumber,
    })

    if (preparedProductURL) {
      window.location.href = preparedProductURL
    } else {
      // during development time, we saw some missing data, which resulted in an empty URL
      console.warn(
        'Insufficient data to prepare product URL',
        materialId,
        manufacturerName,
        description,
        manufacturerPartNumber
      )
    }
  }, [tabType, firstSearchExecuted, isSearching, products])
}
